import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PersistConfig, persistReducer } from 'redux-persist';

import { getReduxStorageUtil } from '../../../main/utils/storage/redux-storage.util.ts';

import { ACCOUNTS_SLICE_NAME, Account, AccountsSliceState, Profile } from './accounts.slice.types.ts';
import { changePasswordThunkExtraReducers } from './thunks/change-password.thunk.ts';
import { createChildProfileThunkExtraReducers } from './thunks/create-child-profile.thunk.ts';
import { deleteParentAccountThunkExtraReducers } from './thunks/delete-parent-account.thunk.ts';
import { fetchAndAssignChildProfileThunkExtraReducers } from './thunks/fetch-and-assign-child-profile.thunk.ts';
import { updateAppSettingsThunkExtraReducers } from './thunks/update-app-settings.thunk.ts';
import { updateChildProfileThunkExtraReducers } from './thunks/update-child-profile.thunk.ts';
import { updateParentAccountThunkExtraReducers } from './thunks/update-parent-account.thunk.ts';

export const ACCOUNTS_SLICE_INITIAL_STATE: AccountsSliceState = {
  isLoading: false,
  isUpdateAccountLoading: false,
  isUpdateProfileLoading: false,
  isUpdateAppSettingsLoading: false,
  isDeleteAccountLoading: false,
  availableProfiles: undefined,
  currentAccount: undefined,
  currentProfile: undefined,
  shouldSeeBeginJourney: false,
  formProfileName: '',
  formProfileAge: 0,
  isParentInfoVisible: false,
};

const persistKeys: (keyof AccountsSliceState)[] = ['shouldSeeBeginJourney'];

const persistConfig = {
  key: ACCOUNTS_SLICE_NAME,
  storage: getReduxStorageUtil(),
  whitelist: persistKeys,
} as PersistConfig<AccountsSliceState>;

const accountsSlice = createSlice({
  name: ACCOUNTS_SLICE_NAME,
  initialState: ACCOUNTS_SLICE_INITIAL_STATE,
  reducers: {
    resetAccountsSlice: () => ACCOUNTS_SLICE_INITIAL_STATE,
    setCurrentAccount: (state, action: PayloadAction<Account | undefined>) => {
      state.currentAccount = action.payload;
    },
    setAvailableProfiles: (state, action: PayloadAction<Profile[] | undefined>) => {
      state.availableProfiles = action.payload;
    },
    setCurrentProfile: (state, action: PayloadAction<Profile | undefined>) => {
      state.currentProfile = action.payload;
    },
    setShouldSeeBeginJourney: (state, action: PayloadAction<boolean>) => {
      state.shouldSeeBeginJourney = action.payload;
    },
    setFormProfileName: (state, action: PayloadAction<string>) => {
      state.formProfileName = action.payload;
    },
    setFormProfileAge: (state, action: PayloadAction<number>) => {
      state.formProfileAge = action.payload;
    },
    resetProfileForm: (state) => {
      state.formProfileName = '';
      state.formProfileAge = 0;
    },
    setIsParentInfoVisible: (state, action: PayloadAction<boolean>) => {
      state.isParentInfoVisible = action.payload;
    },
  },
  extraReducers: (builder) => {
    createChildProfileThunkExtraReducers(builder);
    fetchAndAssignChildProfileThunkExtraReducers(builder);
    updateParentAccountThunkExtraReducers(builder);
    changePasswordThunkExtraReducers(builder);
    updateChildProfileThunkExtraReducers(builder);
    updateAppSettingsThunkExtraReducers(builder);
    deleteParentAccountThunkExtraReducers(builder);
  },
});

export const {
  setCurrentAccount,
  setAvailableProfiles,
  setCurrentProfile,
  setShouldSeeBeginJourney,
  setFormProfileName,
  setFormProfileAge,
  resetProfileForm,
  resetAccountsSlice,
  setIsParentInfoVisible,
} = accountsSlice.actions;

export const AccountsSlice = persistReducer(persistConfig, accountsSlice.reducer);
